import guid from '@whiz-cart/node-shared/guid';
import type { OptionalArgIfPartial } from '@whiz-cart/node-shared/models/helpers/optionalArgIfPartial';
import { Store, createScope, createStore } from 'cross-state';
import { ScopeProvider } from 'cross-state/react';
import { ElementType, ReactNode, createContext, useContext, useMemo } from 'react';

export interface ImperativeDialogProps<T = void> {
    open: boolean;
    onClose: (result?: T) => void;
}

export type ImperativeDialog<T = void> = ElementType<ImperativeDialogProps<T>>;

interface ImperativeDialogInstance<T, P> {
    id: string;
    component: ElementType<ImperativeDialogProps<T> & P>;
    props?: P;
    resolve: (value?: T) => void;
    reject: (reason: unknown) => void;
}

const dialogScope = createScope({
    stack: [] as ImperativeDialogInstance<any, any>[],
});

const globalDialogs = createStore(dialogScope.defaultValue);

export const DialogCloseContext = createContext<(() => void) | undefined>(undefined);
export function useDialogClose() {
    return useContext(DialogCloseContext);
}

export function DialogProvider({ global, children }: { global?: boolean; children?: ReactNode }) {
    const store = useMemo(() => (global ? globalDialogs : createStore(dialogScope.defaultValue)), [global]);
    const active = store.map((state) => state.stack[0]).useStore();

    function close(value?: any) {
        active?.resolve(value);
        store.update((state) => {
            state.stack.pop();
        });
    }

    return (
        <ScopeProvider scope={dialogScope} store={store}>
            {active && (
                <DialogCloseContext.Provider value={close}>
                    <active.component {...active.props} open onClose={close} />
                </DialogCloseContext.Provider>
            )}

            {children}
        </ScopeProvider>
    );
}

export function useDialog() {
    const store = dialogScope.useScope();
    return createOpenFunction(store);
}

export const openDialog = createOpenFunction(globalDialogs);

type PropsArg<TProps extends ImperativeDialogProps<any>> = OptionalArgIfPartial<Omit<TProps, keyof ImperativeDialogProps<any>>>;

type Result<TProps> = Promise<(TProps extends ImperativeDialogProps<infer T> ? T : never) | undefined> & {
    close: () => void;
};

function createOpenFunction(
    store: Store<{
        stack: ImperativeDialogInstance<any, any>[];
    }>,
) {
    function open<TProps extends ImperativeDialogProps<any>>(component: ElementType<TProps>, ...[props]: PropsArg<TProps>): Result<TProps> {
        const id = guid();

        const promise = new Promise<unknown | undefined>((resolve, reject) => {
            store.update((state) => {
                state.stack.push({ id, component, props, resolve, reject });
            });
        });

        return Object.assign(promise, {
            close: () => {
                store.update((state) => {
                    state.stack = state.stack.filter((d) => d.id !== id);
                });
            },
        }) as Result<TProps>;
    }

    return open;
}
